import { call, put, takeLatest } from "redux-saga/effects";
import {
  fetchCategoriesRequest,
  fetchCategoriesSuccess,
  fetchCategoriesFailure,
  submitCategoryRequest,
  submitCategorySuccess,
  submitCategoryFailure,
  deleteCategoryRequest,
  deleteCategorySuccess,
  deleteCategoryFailure,
} from "../slices/category.slice";
import { ApiError } from "@/lib/apiClient";
import { ApiResponse, PaginatedApiResponse } from "@/lib/apiClient";
import {
  deleteCategories,
  getCategories,
  postCategories,
  putCategories,
} from "../actions/category.actions";
import { Category } from "@/types/models/category";

function* fetchCategoriesSaga(
  action: ReturnType<typeof fetchCategoriesRequest>,
): Generator<unknown, void, unknown> {
  try {
    const data = (yield call(getCategories, {
      page: action.payload.page,
      pageSize: action.payload.pageSize,
    })) as PaginatedApiResponse<Category[]>;
    yield put(
      fetchCategoriesSuccess({
        list: data.data,
        pagination: data.pagination,
      }),
    );
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      yield put(fetchCategoriesFailure(err));
    } else {
      yield put(
        fetchCategoriesFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Submit category (create or update)
function* submitCategorySaga(
  action: ReturnType<typeof submitCategoryRequest>,
): Generator<unknown, void, unknown> {
  try {
    const { data, collectionId } = action.payload;
    let ret: ApiResponse<Category>;
    if (collectionId) {
      // Update category
      ret = (yield call(putCategories, {
        collectionId,
        data,
      })) as ApiResponse<Category>;
    } else {
      // Create new category
      ret = (yield call(postCategories, { data })) as ApiResponse<Category>;
    }

    yield put(submitCategorySuccess({ data: ret.data }));
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      yield put(submitCategoryFailure(err));
    } else {
      yield put(
        submitCategoryFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Delete category
function* deleteCategorySaga(
  action: ReturnType<typeof deleteCategoryRequest>,
): Generator<unknown, void, unknown> {
  try {
    const { id } = action.payload;
    yield call(deleteCategories, { id });
    yield put(deleteCategorySuccess({ id }));
  } catch (err: unknown) {
    if (err instanceof ApiError) {
      yield put(deleteCategoryFailure(err));
    } else {
      yield put(
        deleteCategoryFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

export default function* CategoriesRootSaga() {
  yield takeLatest(fetchCategoriesRequest.type, fetchCategoriesSaga);
  yield takeLatest(submitCategoryRequest.type, submitCategorySaga);
  yield takeLatest(deleteCategoryRequest.type, deleteCategorySaga);
}
